import {Box, Button, createListCollection, Field, Heading, Input, Show, Stack, Table} from "@chakra-ui/react";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {dataApi, PayoutList as PayoutLst, PayoutListType} from "../../api/data-api";
import {DateTime} from "luxon";
import {useToast} from "@chakra-ui/toast";
import {useRecoilState} from "recoil";
import {companyData, showTfaCheck} from "../../components/hooks/recoilData";
import Payout from "./payout";
import {errorNotify} from "../../components/hooks/CommonUtils";

export interface PayoutListExt {
    id: number | null,
    type: PayoutListType,
    name: string,
    companyId: number | null,
}

export const payoutTypeList = createListCollection({
    items: [
        {value: "STANDARD", label: "Standard"},
    ],
    itemToString: (item: any) => `${item.label}`,
    itemToValue: (item: any) => item.value,
});

export const PayoutList = () => {
    const [items, setItems] = useState<PayoutLst[]>([]);
    const toast = useToast();
    const [showForm, setShowForm] = useState<boolean>(false);
    const inputDefault: PayoutListExt = useMemo(() => {
        return {
            id: null,
            type: "STANDARD",
            name: "",
            companyId: null,
        }
    }, []);
    const [inputParams, setInputParams] = useState<PayoutListExt>(inputDefault);
    const [company] = useRecoilState(companyData);
    const [count, setCount] = useState<number>(0);
    const [currentPage] = useState<number>(1);
    const pageSize = 10;
    const [, setTfa] = useRecoilState(showTfaCheck);
    const [show, setShow] = useState<boolean>(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!company)
            return;
        dataApi.payoutListOwnListCount(company.ID)
            .then(o => {
                if (o && o > 0)
                    setCount(o as number);
            })
            .catch((err) => errorNotify(err, toast));
    }, [toast, company]);
    useEffect(() => {
        if (!company)
            return;
        dataApi.payoutListOwnList(company.ID, (currentPage - 1) * pageSize, pageSize)
            .then(o => {
                if (o)
                    setItems(o);
            })
            .catch((err) => errorNotify(err, toast));
    }, [count, currentPage, toast, company]);

    const payoutConfirm = useCallback((it: PayoutLst) => {
        if (!it)
            return;
        dataApi.payoutListConfirm(it.ID)
            .then(o => {
                if (!o)
                    return null;
                return dataApi.payoutListGet(it.ID);
            })
            .then(o => {
                if (!o)
                    return null;
                setItems(prev => {
                    const lst = [...prev];
                    let index = lst.indexOf(it);
                    if (index > -1)
                        lst[index] = o;
                    return lst;
                });
                const p = dataApi.documentSign(o.DOCUMENT_ID);
                setTfa(true);
                setLoading(true);
                return p;
            })
            .then((o) => {
                setTfa(false);
                setLoading(false);
                if (o) {
                    toast({title: "Confirm", status: "success"});
                } else {
                    toast({title: "Confirm failed", status: "error"});
                }
            })
            .catch((err) => {
                setTfa(false);
                setLoading(false);
                console.log("Error status code is: " + err)
                toast({title: "Error", description: `code: [${err}]`, status: "error"});
            });
    }, [setTfa, toast]);

    const payoutDelete = useCallback((it: PayoutLst) => {
        if (!it)
            return;
        dataApi.payoutListDelete(it.ID)
            .then((o) => {
                if (o) {
                    toast({title: "Delete", status: "success"});
                    setItems(prev => {
                        return prev.filter(o => o.ID !== it.ID);
                    })
                } else {
                    toast({title: "Delete failed", status: "error"});
                }
            })
            .catch((err) => errorNotify(err, toast));
    }, [toast]);

    const prepareInput = useCallback((item: PayoutLst | null) => {
        setInputParams({
            id: item ? item.ID : null,
            type: item ? item.TYPE1 : "STANDARD",
            name: item ? item.NAME : "",
            companyId: item ? item.COMPANY_ID : (company ? company.ID : null),
        });
    }, [company]);

    const createOrUpdate = useCallback(() => {
        let companyId = inputParams.companyId;
        if (!companyId) {
            if (company)
                companyId = company.ID;
        }
        if (!companyId)
            return;
        dataApi.payoutListCreateOrUpdate(inputParams.id, inputParams.name, inputParams.type, companyId)
            .then((value) => {
                toast({title: "Save", status: "success"});
                setItems(prev => {
                    const lst = [...prev];
                    if (inputParams.id) {
                        let findO = lst.find(o => o.ID === value.ID);
                        if (findO)
                            lst[lst.indexOf(findO)] = value;
                    } else {
                        lst.push(value);
                    }
                    return lst;
                });
                if (!inputParams.id) {
                    setInputParams(prev => {
                        return {...prev, id: value.ID};
                    });
                }
                prepareInput(null);
                setShowForm(false);
            })
            .catch((err) => errorNotify(err, toast));
    }, [inputParams, company, toast, prepareInput]);

    const showDlg = useCallback((item: PayoutLst) => {
        prepareInput(item);
        setShow(!!item)
    }, [prepareInput]);

    return (
        <Stack width="full" gap="5">
            <Heading size="xl">Списки выплат</Heading>
            <Payout payoutListId={inputParams.id} show={show} setShow={setShow}/>
            <Box>
                <Button onClick={e => {
                    if (showForm) {
                        setShowForm(false);
                    } else {
                        prepareInput(null);
                        setShowForm(true);
                    }
                }}>
                    Создать новый
                </Button>
            </Box>
            <Show when={showForm}>
                <Box borderWidth={1} borderColor="gray">
                    <Field.Root orientation="horizontal" required>
                        <Field.Label>Имя <Field.RequiredIndicator/></Field.Label>
                        <Input type="text" placeholder="name"
                               value={inputParams.name}
                               onChange={(it) => setInputParams((prev) => {
                                   return {...prev, name: it.target.value}
                               })}/>
                    </Field.Root>
                    {/*<Field.Root orientation="horizontal" required>*/}
                    {/*    <Field.Label>type <Field.RequiredIndicator/></Field.Label>*/}
                    {/*    <Select.Root defaultValue={[inputParams.type]} collection={payoutTypeList}*/}
                    {/*                 onChange={(e: any) => setInputParams(prev => {*/}
                    {/*                     return {*/}
                    {/*                         ...prev, type: e.target.value*/}
                    {/*                     }*/}
                    {/*                 })}>*/}
                    {/*        <Select.HiddenSelect/>*/}
                    {/*        <Select.Control>*/}
                    {/*            <Select.Trigger>*/}
                    {/*                <Select.ValueText placeholder="type"/>*/}
                    {/*            </Select.Trigger>*/}
                    {/*            <Select.IndicatorGroup>*/}
                    {/*                <Select.Indicator/>*/}
                    {/*            </Select.IndicatorGroup>*/}
                    {/*        </Select.Control>*/}
                    {/*        <Portal>*/}
                    {/*            <Select.Positioner>*/}
                    {/*                <Select.Content>*/}
                    {/*                    {payoutTypeList.items.map((it: any) => (*/}
                    {/*                        <Select.Item item={it} key={it.value}>*/}
                    {/*                            {it.label}*/}
                    {/*                            <Select.ItemIndicator/>*/}
                    {/*                        </Select.Item>*/}
                    {/*                    ))}*/}
                    {/*                </Select.Content>*/}
                    {/*            </Select.Positioner>*/}
                    {/*        </Portal>*/}
                    {/*    </Select.Root>*/}
                    {/*</Field.Root>*/}
                    <Button borderRadius={0} variant="solid" colorScheme="teal" width="full"
                            onClick={e => createOrUpdate()}>
                        {inputParams.id ? "Сохранить" : "Создать"}
                    </Button>
                </Box>
            </Show>
            <Table.Root size="sm" variant="outline" striped>
                <Table.Header>
                    <Table.Row>
                        <Table.ColumnHeader>№</Table.ColumnHeader>
                        <Table.ColumnHeader>Имя</Table.ColumnHeader>
                        <Table.ColumnHeader>Тип</Table.ColumnHeader>
                        <Table.ColumnHeader>Документ</Table.ColumnHeader>
                        <Table.ColumnHeader>Создан</Table.ColumnHeader>
                        <Table.ColumnHeader>Изменен</Table.ColumnHeader>
                        <Table.ColumnHeader>Отправлен</Table.ColumnHeader>
                        <Table.ColumnHeader>Подтвержден</Table.ColumnHeader>
                        <Table.ColumnHeader>Обработан</Table.ColumnHeader>
                        <Table.ColumnHeader>Действия</Table.ColumnHeader>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {items.map((item) => (
                        <Table.Row key={item.ID}>
                            <Table.Cell>{item.ID}</Table.Cell>
                            <Table.Cell>{item.NAME}</Table.Cell>
                            <Table.Cell>{item.TYPE1}</Table.Cell>
                            <Table.Cell>{item.DOCUMENT_ID}</Table.Cell>
                            <Table.Cell>{item.CREATED ? DateTime.fromMillis(item.CREATED).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>{item.VERSION ? DateTime.fromMillis(item.VERSION).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>{item.SENDED ? DateTime.fromMillis(item.SENDED).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>{item.CONFIRMED ? DateTime.fromMillis(item.CONFIRMED).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>{item.PROCESSED ? DateTime.fromMillis(item.PROCESSED).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>
                                <Button onClick={e => showDlg(item)}>
                                    Выплаты
                                </Button>
                                <Show when={!item.DOCUMENT_ID && !item.SENDED && !item.PROCESSED}>
                                    <Button onClick={e => {
                                        if (showForm) {
                                            setShowForm(false);
                                        } else {
                                            prepareInput(item);
                                            setShowForm(true);
                                        }
                                    }}>Редактировать</Button>
                                    <Button loading={loading} onClick={e => payoutConfirm(item)}>Подтвердить</Button>
                                    <Button loading={loading} onClick={e => payoutDelete(item)}>Удалить</Button>
                                </Show>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table.Root>

        </Stack>
    );
};